import { ChevronDownIcon } from "@radix-ui/react-icons";
import { Check, Loader2 } from "lucide-react";
import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useCustomChannels } from "@/hooks/api/use-custom-channels";
import { useCustomChannelSelection } from "@/hooks/use-custom-channel-selection";
import { useSelectedRulesetId } from "@/hooks/use-selected-ruleset";
import { cn } from "@/lib/utils";

export function CustomSubchannelSelector() {
  const { selectedChannel, selectedSubchannel, setSelectedSubchannel } =
    useCustomChannelSelection();
  const selectedRulesetId = useSelectedRulesetId();
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Fetch custom channels with subchannels for the selected channel
  const { data: channelsData, isLoading: isLoadingSubchannels } =
    useCustomChannels({
      extra_fields: "custom_subchannel",
      limit: 1000,
      rule_set_id: selectedRulesetId ?? undefined,
    });

  // Get unique subchannels for the selected channel
  const subchannels = useMemo(() => {
    if (!selectedChannel) return [];
    const results = channelsData?.results || [];
    const channelSubchannels = results
      .filter((c) => c.custom_channel === selectedChannel)
      .map((c) => c.custom_subchannel)
      .filter((sc): sc is string => !!sc);

    // Get unique subchannels
    const uniqueSubchannels = Array.from(new Set(channelSubchannels)).map(
      (name) => ({ custom_subchannel: name }),
    );
    return uniqueSubchannels;
  }, [channelsData, selectedChannel]);

  // Filter subchannels based on search query
  const filteredSubchannels = useMemo(() => {
    if (!searchQuery) return subchannels;
    const query = searchQuery.toLowerCase();
    return subchannels.filter((sc) =>
      sc.custom_subchannel.toLowerCase().includes(query),
    );
  }, [subchannels, searchQuery]);

  const selectedSubchannelObj = subchannels.find(
    (sc) => sc.custom_subchannel === selectedSubchannel,
  );

  // Subchannel selection handler
  const handleSubchannelSelect = (subchannelName: string) => {
    setSelectedSubchannel(subchannelName);
    setOpen(false);
  };

  // Clear subchannel selection
  const handleClearSubchannel = () => {
    setSelectedSubchannel(undefined);
    setOpen(false);
  };

  // Don't render if no channel is selected or if there are no subchannels
  if (!selectedChannel || subchannels.length === 0) {
    return null;
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="justify-between gap-6 min-w-[300px] whitespace-nowrap overflow-hidden bg-white dark:bg-[hsl(195_30%_12%)]"
        >
          {selectedSubchannelObj
            ? selectedSubchannelObj.custom_subchannel
            : "All sub-channels"}
          <ChevronDownIcon className="h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[360px]" align="start">
        <Command>
          <CommandInput
            placeholder="Search sub-channels..."
            value={searchQuery}
            onValueChange={setSearchQuery}
          />
          <CommandList>
            {isLoadingSubchannels && (
              <div className="py-6 text-center">
                <Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" />
                <p className="text-sm text-muted-foreground mt-2">
                  Loading sub-channels...
                </p>
              </div>
            )}

            {!isLoadingSubchannels && filteredSubchannels.length === 0 && (
              <CommandEmpty>No sub-channels found.</CommandEmpty>
            )}

            {!isLoadingSubchannels && filteredSubchannels.length > 0 && (
              <CommandGroup>
                <CommandItem
                  value="__all__"
                  onSelect={handleClearSubchannel}
                  className="font-medium"
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      !selectedSubchannel ? "opacity-100" : "opacity-0",
                    )}
                  />
                  All sub-channels
                </CommandItem>

                {filteredSubchannels.map((subchannel) => (
                  <CommandItem
                    key={subchannel.custom_subchannel}
                    value={subchannel.custom_subchannel}
                    onSelect={() =>
                      handleSubchannelSelect(subchannel.custom_subchannel)
                    }
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        selectedSubchannel === subchannel.custom_subchannel
                          ? "opacity-100"
                          : "opacity-0",
                      )}
                    />
                    {subchannel.custom_subchannel}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
